import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MasterSelect } from "@/components/MasterSelect";
import { FabricImage } from "@/components/FabricImage";
import { compressImage } from "@/lib/compressImage";
import { toast } from "sonner";
import { Camera, X, Check, Loader2 } from "lucide-react";

const L = ({ children }) => (
  <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{children}</Label>
);

export default function QuickAdd({ profile }) {
  const [items, setItems] = useState([]);
  const [masters, setMasters] = useState({ company: [], quality: [] });
  const [name, setName] = useState("");
  const [shade, setShade] = useState("");
  const [company, setCompany] = useState("");
  const [quality, setQuality] = useState("");
  const [imagePath, setImagePath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const camRef = useRef(null);

  useEffect(() => {
    api.get("/items").then(({ data }) => setItems(data)).catch(() => {});
    Promise.all([api.get("/masters/company"), api.get("/masters/quality")])
      .then(([c, q]) => setMasters({ company: c.data.map((m) => m.name), quality: q.data.map((m) => m.name) }))
      .catch(() => {});
  }, []);

  const typed = name.trim().toLowerCase();
  const match = items.find((i) => (i.name || "").trim().toLowerCase() === typed);
  const suggestions = typed && !match
    ? items.filter((i) => (i.name || "").toLowerCase().includes(typed)).slice(0, 6)
    : [];

  const pickImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/upload", fd);
      setImagePath(data.image_path);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!imagePath) return toast.error("Take or choose a photo first");
    if (!name.trim()) return toast.error("Item name is needed");
    setSaving(true);
    try {
      const { data } = await api.post("/quick-add", {
        item_name: name.trim(), shade: shade.trim(), company, quality, image_path: imagePath,
      });
      toast.success(data.item_created ? "New item created with photo" : "Photo mapped to existing item");
      setRecent((r) => [{ ...data, name: data.item.name }, ...r].slice(0, 8));
      if (data.item_created) setItems((prev) => [...prev, data.item]);
      setImagePath(null);
      setShade("");
      if (data.item_created) { setCompany(""); setQuality(""); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell profile={profile}>
      <div className="mx-auto w-full max-w-lg px-4 py-6" data-testid="quick-add-page">
        <h1 className="font-head text-3xl font-extrabold tracking-tighter">Quick add</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Snap a shade photo, type the item — an existing item is mapped automatically.
        </p>

        <form onSubmit={save} className="mt-5 space-y-4">
          <div
            data-testid="quick-add-photo-zone"
            onClick={() => camRef.current?.click()}
            className="flex min-h-[190px] cursor-pointer items-center justify-center border-2 border-dashed border-zinc-300 bg-zinc-50 p-3 active:border-zinc-950"
          >
            {imagePath ? (
              <div className="relative w-full">
                <FabricImage path={imagePath} alt="Shade" className="h-48 w-full object-cover" />
                <button
                  type="button"
                  data-testid="quick-add-photo-remove"
                  onClick={(e) => { e.stopPropagation(); setImagePath(null); }}
                  className="absolute right-1 top-1 flex h-8 w-8 items-center justify-center border border-zinc-300 bg-white text-zinc-600"
                >
                  <X size={15} />
                </button>
              </div>
            ) : (
              <div className="text-center text-zinc-500">
                {uploading ? (
                  <Loader2 className="mx-auto mb-2 animate-spin" size={26} />
                ) : (
                  <Camera className="mx-auto mb-2" size={26} />
                )}
                <p className="text-sm font-semibold">{uploading ? "Uploading…" : "Tap to take a photo"}</p>
                <p className="mt-0.5 text-xs">Camera or gallery</p>
              </div>
            )}
            <input
              ref={camRef}
              data-testid="quick-add-photo-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => pickImage(e.target.files?.[0])}
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <L>Item name</L>
              {name.trim() && (
                <span
                  data-testid="quick-add-match-badge"
                  className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    match ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {match ? "Existing item" : "New item"}
                </span>
              )}
            </div>
            <Input
              data-testid="quick-add-name-input"
              className="mt-1 h-11 rounded-sm text-base uppercase"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ROYAL TR-6234"
            />
            {suggestions.length > 0 && (
              <div className="mt-1 border border-zinc-200" data-testid="quick-add-suggestions">
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`quick-add-suggestion-${s.id}`}
                    onClick={() => { setName(s.name); setCompany(s.company || ""); setQuality(s.quality || ""); }}
                    className="flex w-full items-center justify-between border-b border-zinc-100 px-3 py-2 text-left text-sm last:border-0 active:bg-zinc-100"
                  >
                    <span className="font-semibold">{s.name}</span>
                    <span className="text-xs text-zinc-500">{s.company || "—"}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <L>Shade</L>
            <Input
              data-testid="quick-add-shade-input"
              className="mt-1 h-11 rounded-sm text-base"
              value={shade}
              onChange={(e) => setShade(e.target.value)}
              placeholder="101"
            />
          </div>

          {!match && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <L>Company</L>
                <MasterSelect className="mt-1 h-11 rounded-sm" value={company} options={masters.company}
                  onChange={setCompany} testId="quick-add-company" />
              </div>
              <div>
                <L>Quality</L>
                <MasterSelect className="mt-1 h-11 rounded-sm" value={quality} options={masters.quality}
                  onChange={setQuality} testId="quick-add-quality" />
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={saving || uploading}
            data-testid="quick-add-save-btn"
            className="h-12 w-full rounded-sm bg-[var(--mod-primary)] text-base text-white hover:opacity-90"
          >
            {saving ? "Saving…" : match ? "Map photo to item" : "Create item with photo"}
          </Button>
        </form>

        {recent.length > 0 && (
          <div className="mt-7" data-testid="quick-add-recent">
            <L>Added just now</L>
            <div className="mt-2 divide-y divide-zinc-100 border border-zinc-200">
              {recent.map((r, i) => (
                <div key={`${r.fabric_id}-${i}`} className="flex items-center gap-3 px-3 py-2">
                  <FabricImage path={r.image_path} alt={r.name} className="h-10 w-10 object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{r.name}</p>
                    <p className="text-xs text-zinc-500">{r.shade ? `Shade ${r.shade}` : "No shade"}</p>
                  </div>
                  <Check size={15} className="text-emerald-600" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
